import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Outlet, useLocation, useSearchParams } from "react-router-dom";
import { z } from "zod";

import { onboardingQueriesStore } from "@/features/onboarding/api/queries";
import { useStoreUrlParams } from "@/features/onboarding/hooks";
import { useEverflowClick } from "@/features/onboarding/hooks/use-everflow-click";
import { IS_CALL_CENTER_PARAM_KEY } from "@/features/onboarding/models/constants";
import { onboardingUserSchema } from "@/features/onboarding/models/schemas";
import { useOnboardingStore } from "@/features/onboarding/stores/use-onboarding-store";
import { LogoLoader } from "@/shared/components";
import { PRODUCT_SLUG } from "@/shared/domains";
import { useLanguageFromParams } from "@/shared/hooks";
import { useMouseflowSession } from "@/shared/lib/mouseflow/hooks/use-mouseflow-session";
import { Layout } from "@/shared/ui";
import { LONGEVITY_ROUTE } from "../router/constants";
import {
  parseLongevityFrequency,
  useLongevityStore,
} from "../stores/use-longevity-store";

const LONGEVITY_STEPS = [
  LONGEVITY_ROUTE.ACCOUNT_PERSONAL_DETAILS,
  LONGEVITY_ROUTE.ACCOUNT_CONTACT_DETAILS,
  LONGEVITY_ROUTE.CHECKOUT,
];

export const LongevityLayout = () => {
  useLanguageFromParams();
  useEverflowClick();
  useStoreUrlParams();
  useMouseflowSession();

  const [searchParams] = useSearchParams();
  const setLongevityParams = useLongevityStore(
    (state) => state.setLongevityParams,
  );

  const setIsCallCenter = useOnboardingStore((state) => state.setIsCallCenter);
  const patchPersonalDetails = useOnboardingStore(
    (state) => state.patchPersonalDetails,
  );
  const patchContactDetails = useOnboardingStore(
    (state) => state.patchContactDetails,
  );

  const userCode = onboardingUserSchema.shape.code.safeParse(
    searchParams.get("code"),
  ).data;

  const [isIdentityHydrated, setIsIdentityHydrated] = useState(!userCode);

  const {
    data: onboardingUser,
    isSuccess: isSuccessOnboardingUser,
    isError: isErrorOnboardingUser,
  } = useQuery({
    ...onboardingQueriesStore.onboarding.user(userCode),
    enabled: Boolean(userCode),
  });

  useEffect(() => {
    const productParam = searchParams.get("product");
    const validProductSlugs = Object.values(PRODUCT_SLUG) as string[];
    const productSlug =
      productParam !== null && validProductSlugs.includes(productParam)
        ? productParam
        : PRODUCT_SLUG.SERMORELIN;
    const frequency =
      parseLongevityFrequency(searchParams.get("frequency")) ?? 1;

    setLongevityParams({ productSlug, frequency });

    const isCallCenterParam = z
      .stringbool()
      .safeParse(searchParams.get(IS_CALL_CENTER_PARAM_KEY)).data;

    if (isCallCenterParam !== undefined) {
      setIsCallCenter(isCallCenterParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isSuccessOnboardingUser) {
      patchPersonalDetails({ firstName: onboardingUser.firstName });
      patchContactDetails({
        code: onboardingUser.code,
        email: onboardingUser.email,
        phoneNumber: onboardingUser.phoneNumber,
      });
    }

    if (isSuccessOnboardingUser || isErrorOnboardingUser) {
      setIsIdentityHydrated(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessOnboardingUser, isErrorOnboardingUser]);

  const { pathname } = useLocation();

  const currentStepIndex = LONGEVITY_STEPS.findIndex((step) =>
    pathname.startsWith(step),
  );

  const progress = ((currentStepIndex + 1) / LONGEVITY_STEPS.length) * 100;

  if (!isIdentityHydrated) {
    return <LogoLoader show fullscreen />;
  }

  return (
    <div className="flex h-dvh flex-col bg-white">
      <Layout.Header progress={progress} />
      <Outlet />
    </div>
  );
};
